// Filter types and interfaces

export type FilterType = 'sender' | 'recipient' | 'subject' | 'body' | 'labels' | 'gmail_category' | 'gmail_status' | 'sender_history' | 'sender_blocked';

export type LogicOperator = 'AND' | 'OR';

export type OperatorType =
  | 'contains'
  | 'does_not_contain'
  | 'equals'
  | 'does_not_equal'
  | 'starts_with'
  | 'ends_with'
  | 'has_label'
  | 'does_not_have_label'
  | 'is_category'
  | 'is_not_category'
  | 'is_status'
  | 'is_not_status'
  | 'has_sent_before'
  | 'has_never_sent_before'
  | 'sent_count_greater_than'
  | 'sent_count_less_than'
  | 'is_blocked'
  | 'is_not_blocked';

export interface Condition {
  id: string;
  field: string;
  operator: OperatorType;
  value: string;
  caseSensitive?: boolean;
  timeWindowDays?: number; // For sender_history operators
}

export interface ConditionGroup {
  id: string;
  logicOperator: LogicOperator;
  conditions: Condition[];
}

export interface Filter {
  id: number;
  name: string;
  order: number;
  logicOperator: LogicOperator;
  conditions: Condition[];
  conditionGroups: ConditionGroup[];
  optional: boolean;
}

// Filter type metadata
export interface FilterTypeMetadata {
  value: FilterType;
  label: string;
  description: string;
  field: string;
  operators: OperatorType[];
}

export const FILTER_TYPES: FilterTypeMetadata[] = [
  {
    value: 'sender',
    label: "Sender's from email address",
    description: 'Filter by email sender address',
    field: 'from_email',
    operators: ['contains', 'does_not_contain', 'equals', 'does_not_equal', 'starts_with', 'ends_with']
  },
  {
    value: 'sender_history',
    label: "Sender's history",
    description: 'Filter based on sender email history',
    field: 'sender_history',
    operators: ['has_sent_before', 'has_never_sent_before', 'sent_count_greater_than', 'sent_count_less_than']
  },
  {
    value: 'sender_blocked',
    label: "Sender's blocked status",
    description: 'Filter by whether sender is on your block list',
    field: 'sender_blocked',
    operators: ['is_blocked', 'is_not_blocked']
  },
  {
    value: 'recipient',
    label: 'To email address',
    description: 'Filter by email recipient address',
    field: 'to_email',
    operators: ['contains', 'does_not_contain', 'equals', 'does_not_equal', 'starts_with', 'ends_with']
  },
  {
    value: 'subject',
    label: 'Subject line',
    description: 'Filter by email subject',
    field: 'subject',
    operators: ['contains', 'does_not_contain', 'equals', 'does_not_equal', 'starts_with', 'ends_with']
  },
  {
    value: 'body',
    label: 'Email body',
    description: 'Filter by email body content',
    field: 'body_text',
    operators: ['contains', 'does_not_contain']
  },
  {
    value: 'labels',
    label: 'Gmail labels',
    description: 'Filter by user-created Gmail labels',
    field: 'labels',
    operators: ['has_label', 'does_not_have_label']
  },
  {
    value: 'gmail_category',
    label: 'Gmail categorized',
    description: 'Filter by Gmail category (Primary, Social, Promotions, etc.)',
    field: 'category',
    operators: ['is_category', 'is_not_category']
  },
  {
    value: 'gmail_status',
    label: 'Gmail Status',
    description: 'Filter by Gmail status (Inbox, Starred, Important, etc.)',
    field: 'status',
    operators: ['is_status', 'is_not_status']
  }
];

// Operator metadata
export interface OperatorMetadata {
  value: OperatorType;
  label: string;
  requiresValue: boolean;
  requiresTimeWindow?: boolean;
}

export const OPERATORS: Record<OperatorType, OperatorMetadata> = {
  contains: {
    value: 'contains',
    label: 'Contains',
    requiresValue: true
  },
  does_not_contain: {
    value: 'does_not_contain',
    label: 'Does not contain',
    requiresValue: true
  },
  equals: {
    value: 'equals',
    label: 'Equals',
    requiresValue: true
  },
  does_not_equal: {
    value: 'does_not_equal',
    label: 'Does not equal',
    requiresValue: true
  },
  starts_with: {
    value: 'starts_with',
    label: 'Starts with',
    requiresValue: true
  },
  ends_with: {
    value: 'ends_with',
    label: 'Ends with',
    requiresValue: true
  },
  has_label: {
    value: 'has_label',
    label: 'Has label',
    requiresValue: true
  },
  does_not_have_label: {
    value: 'does_not_have_label',
    label: 'Does not have label',
    requiresValue: true
  },
  is_category: {
    value: 'is_category',
    label: 'Is category',
    requiresValue: true
  },
  is_not_category: {
    value: 'is_not_category',
    label: 'Is not category',
    requiresValue: true
  },
  is_status: {
    value: 'is_status',
    label: 'Is status',
    requiresValue: true
  },
  is_not_status: {
    value: 'is_not_status',
    label: 'Is not status',
    requiresValue: true
  },
  has_sent_before: {
    value: 'has_sent_before',
    label: 'Has sent before',
    requiresValue: false
  },
  has_never_sent_before: {
    value: 'has_never_sent_before',
    label: 'Has never sent before',
    requiresValue: false
  },
  sent_count_greater_than: {
    value: 'sent_count_greater_than',
    label: 'Sent more than',
    requiresValue: true,
    requiresTimeWindow: true
  },
  sent_count_less_than: {
    value: 'sent_count_less_than',
    label: 'Sent less than',
    requiresValue: true,
    requiresTimeWindow: true
  },
  is_blocked: {
    value: 'is_blocked',
    label: 'Sender is on my block list',
    requiresValue: false
  },
  is_not_blocked: {
    value: 'is_not_blocked',
    label: 'Sender is not on my block list',
    requiresValue: false
  }
};

// Helper functions
export function getFilterTypeMetadata(type: FilterType): FilterTypeMetadata | undefined {
  return FILTER_TYPES.find(ft => ft.value === type);
}

export function getFilterTypeMetadataByField(field: string): FilterTypeMetadata | undefined {
  return FILTER_TYPES.find(ft => ft.field === field);
}

export function getFilterTypeFromField(field: string): FilterType | undefined {
  const metadata = getFilterTypeMetadataByField(field);
  return metadata?.value;
}

export function getOperatorsForFilterType(type: FilterType): OperatorType[] {
  const metadata = getFilterTypeMetadata(type);
  return metadata?.operators || [];
}

export function getOperatorsForField(field: string): OperatorType[] {
  const metadata = getFilterTypeMetadataByField(field);
  return metadata?.operators || [];
}

export function getOperatorMetadata(operator: OperatorType): OperatorMetadata {
  return OPERATORS[operator];
}

export function generateConditionId(): string {
  return `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export function generateGroupId(): string {
  return `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
